import React, { useState, useEffect, useMemo } from "react";
import "./index.scss";
import { useSelector } from "react-redux";
import CustomCard from "../../../../components/CustomCard";
import CustomLayout from "../../../../components/CustomLayout";
import CustomButton from "../../../../components/CustomButton";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  containerVariants,
  cardVariants
} from "../../../../assets/framer/styles";

// 1) Import i18n
import { useTranslation } from "react-i18next";
import { generateDonationSchedule } from "../SplitDonation";
import { ACTIVE_FUNDS } from "../../../../utils/constants";
import {
  getCanonicalTemplateKey,
  isDhulHijjahTemplateKey,
  isFridayTemplateKey,
  isZakatTemplateKey
} from "../../../../utils/templateUtils";

const SelectCause = () => {
  // 2) Destructure t from useTranslation
  const { t } = useTranslation();

  const Funds = useSelector((state) => state.donation.funds);
  const [breakdown, setBreakdown] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const template = searchParams.get("template") || "";
  const templateKey = getCanonicalTemplateKey(template);
  const appealCode =
    searchParams.get("appealCode") || searchParams.get("appeal") || "";
  const isLast10NightsAppeal = appealCode.toLowerCase() === "last10nights";
  const ref = searchParams.get("ref") || "";
  const [fridayTotal, setFridayTotal] = useState(
    ref && ref.toLowerCase().includes("ogn") ? 25 : 10
  );
  const [selectedFunds, setSelectedFunds] = useState([]);
  const timezone = useSelector((state) => state.donation.timezone);

  const startDate =
    useSelector((state) => state.donation.startDate) || new Date();
  const endDate = useSelector((state) => state.donation.endDate);
  const hourOfDonation =
    useSelector((state) => state.donation.hourOfDonation) || 22;
  const interval = useSelector((state) => state.donation.interval);
  const userTimezoneName = timezone || "UTC";

  useEffect(() => {
    const breakdownParam = searchParams.get("breakdown");
    if (breakdownParam) {
      try {
        const parsedBreakdown = JSON.parse(decodeURIComponent(breakdownParam));
        setBreakdown(parsedBreakdown);
        const fundsSelected = parsedBreakdown.map((i) => i.fundId);
        setSelectedFunds(fundsSelected);
      } catch (e) {
        // ignore parse error
      }
    }
  }, [searchParams]);

  const visibleFunds = useMemo(() => {
    const sourceFunds = Funds && Funds.length > 0 ? Funds : ACTIVE_FUNDS;
    const isZakat = isZakatTemplateKey(templateKey);
    const isFriday = isFridayTemplateKey(templateKey);
    const FUND_NAME_OVERRIDES = {
      "9": "Thirst Relief"
    };

    const base = isZakat && !isFriday
      ? sourceFunds.filter((f) =>
          String(f.name || "")
            .toLowerCase()
            .includes("zakat")
        )
      : sourceFunds;

    return base.map((fund) => {
      const override = FUND_NAME_OVERRIDES[String(fund.fundId)];
      return override ? { ...fund, name: override } : fund;
    });
  }, [templateKey, Funds]);

  // If the user switches into a zakat-only template, drop any previously chosen non-zakat funds
  useEffect(() => {
    if (!isZakatTemplateKey(templateKey)) return;
    setBreakdown((prev) =>
      prev.filter((item) => visibleFunds.some((f) => f.fundId === item.fundId))
    );
    setSelectedFunds((prev) =>
      prev.filter((id) => visibleFunds.some((f) => f.fundId === id))
    );
  }, [templateKey, visibleFunds]);

  const fundsWithAmounts = visibleFunds.map((fund) => {
    const matchingBreakdown = breakdown.find(
      (item) => item.fundId === fund.fundId
    );
    return {
      ...fund,
      amount: matchingBreakdown ? matchingBreakdown.amount : 0
    };
  });

  const handleAmountChange = (fundId, amount) => {
    setBreakdown((prevBreakdown) => {
      const existingIndex = prevBreakdown.findIndex(
        (item) => item.fundId === fundId
      );
      if (existingIndex >= 0) {
        const updatedBreakdown = [...prevBreakdown];
        if (amount === 0) {
          updatedBreakdown.splice(existingIndex, 1);
        } else {
          updatedBreakdown[existingIndex].amount = amount;
        }
        return updatedBreakdown;
      } else {
        if (amount === 0) return prevBreakdown;
        return [...prevBreakdown, { fundId, amount }];
      }
    });
  };

  const handleCheckboxChange = (fundId, checked) => {
    if (checked) {
      setSelectedFunds((prev) => [...prev, fundId]);
    } else {
      setSelectedFunds((prev) => prev.filter((id) => id !== fundId));
    }
  };

  const isFridayTemplate = isFridayTemplateKey(templateKey);
  const isZakatTemplate = isZakatTemplateKey(templateKey);
  const isDhulHijjahTemplate = isDhulHijjahTemplateKey(templateKey);
  const isNextDisabled = isFridayTemplate
    ? selectedFunds.length === 0 || Number(fridayTotal) < 10
    : breakdown.length === 0;

  const handleNext = () => {
    if (!isFridayTemplate && (!breakdown || breakdown.length === 0)) {
      return;
    }

    const totalAmount = breakdown.reduce((prev, curr) => curr.amount + prev, 0);

    if (!isFridayTemplate && totalAmount < 30) {
      // 3) Localize the alert message
      alert(t("selectCauseAlertMin30"));
      return;
    }

    if (isFridayTemplate) {
      const totalWeekly = Number(fridayTotal) || 0;

      if (isFridayTemplate && fridayTotal < 10) {
        // 3) Localize the alert message
        alert(t("selectCauseAlertMin10"));
        return;
      }
      if (selectedFunds.length > 0 && totalWeekly >= 10) {
        const totalForYear = totalWeekly;
        const perFund = totalForYear / selectedFunds.length;
        const newBreakdown = selectedFunds.map((fundId) => ({
          fundId,
          amount: perFund
        }));
        const breakdownString = encodeURIComponent(
          JSON.stringify(newBreakdown)
        );
        const newParams = new URLSearchParams(searchParams);

        const donationScheduleBreakdown = generateDonationSchedule(
          startDate,
          endDate,
          hourOfDonation,
          interval,
          totalForYear,
          0,
          userTimezoneName,
          template
        );

        newParams.set("stage", "3");
        newParams.set("breakdown", breakdownString);

        newParams.set(
          "schedule",
          encodeURIComponent(JSON.stringify(donationScheduleBreakdown))
        );
        newParams.set("scheduleOption", 0);
        setSearchParams(newParams);
      }
      return;
    }

    const breakdownString = encodeURIComponent(JSON.stringify(breakdown));
    const newParams = new URLSearchParams(searchParams);
    newParams.set("stage", "2");
    newParams.set("breakdown", breakdownString);
    setSearchParams(newParams);
  };

  return (
    <CustomLayout
      // 4) Localize the title & description
      title={
        isLast10NightsAppeal
          ? t("selectCauseTitleLast10Nights")
          : isZakatTemplate
          ? t("selectCauseTitleZakat")
          : isDhulHijjahTemplate
          ? t("selectCauseTitleDhulHijjah")
          : t("selectCauseTitle")
      }
      description={
        isFridayTemplate
          ? t("selectCauseFridayDescription")
          : isLast10NightsAppeal
          ? t("selectCauseDescriptionLast10Nights")
          : isZakatTemplate
          ? t("selectCauseDescriptionZakat")
          : isDhulHijjahTemplate
          ? t("selectCauseDescriptionDhulHijjah")
          : t("selectCauseDescription")
      }
      businessTitle={
        ref && ref.toLowerCase().includes("ogn")
          ? t("selectCauseBusinessTitle")
          : undefined
      }
      button={
        <CustomButton
          // 5) Localize the button text
          text={t("nextButton")}
          disabled={isNextDisabled}
          onClick={handleNext}
        />
      }
    >
      {isZakatTemplate ? (
        <motion.div
          className="single-card-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ display: "flex", justifyContent: "center" }}
        >
          {fundsWithAmounts.map((fund) => (
            <motion.div key={fund.fundId} variants={cardVariants}>
              <div
                style={{
                  border: "1px solid #d6d8e1",
                  borderRadius: 16,
                  padding: "24px 28px",
                  background: "#fff",
                  boxShadow: "0 8px 24px rgba(8, 53, 122, 0.08)",
                  maxWidth: 380,
                  textAlign: "center"
                }}
              >
                <p style={{ fontWeight: 700, fontSize: "20px", color: "#0a2a63", marginBottom: 8 }}>
                  {fund.name}
                </p>
                <p style={{ color: "#5b657a", fontSize: "14px", marginBottom: 20, lineHeight: 1.5 }}>
                  {t("fundInfoZakat")}
                </p>
                <div
                  style={{
                    border: "1px solid #d6d8e1",
                    borderRadius: 12,
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "#f7f9fc"
                  }}
                >
                  <span style={{ fontWeight: 700, color: "#0a2a63", fontSize: "20px" }}>$</span>
                  <input
                    type="number"
                    min="0"
                    value={fund.amount || ""}
                    onChange={(e) => handleAmountChange(fund.fundId, Number(e.target.value) || 0)}
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      textAlign: "right",
                      width: "100%",
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#0a2a63",
                      marginLeft: 8
                    }}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <motion.div
          className="three-col-responsive-list-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {fundsWithAmounts.map((fund) => (
            <motion.div
              key={fund.fundId}
              variants={cardVariants}
              style={{
                ...(fund.fundId === "16" && {
                  order: -1
                })
              }}
            >
              <CustomCard
                fund={fund}
                initialAmount={fund.amount}
                onAmountChange={handleAmountChange}
                template={template}
                isFriday={isFridayTemplate}
                selected={selectedFunds.includes(fund.fundId)}
                onCheckboxChange={handleCheckboxChange}
              />
            </motion.div>
          ))}
        </motion.div>
      )}

      {isFridayTemplate && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "8px 12px",
            backgroundColor: "#fff",
            width: "93%"
          }}
        >
          {/* Label inside the border, left side */}
          <label
            htmlFor="fridayTotal"
            style={{ position: "absolute", textAlign: "left" }}
          >
            {t("selectCauseFridayLabel")}
          </label>

          {/* Dollar + input on right side */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1
            }}
          >
            <input
              type="text"
              inputMode="numeric"
              placeholder="0.00"
              value={"$" + fridayTotal}
              onChange={(e) => {
                const numericValue =
                  parseInt(e.target.value.replace(/[^0-9]/g, ""), 10) || 0;
                setFridayTotal(numericValue);
              }}
              style={{
                border: "none",
                outline: "none",
                flex: 1,
                textAlign: "right",
                fontSize: "1.5rem",
                color: "#08357a",
                fontWeight: "bold"
              }}
            />
          </div>
        </div>
      )}
    </CustomLayout>
  );
};

export default SelectCause;
